/**
 * DealModalTabs - Sistema de abas do modal premium
 * Layout compacto com ícones e contadores
 * 
 * Versão: 2.2.0
 * RELEASE: 2026-10-17 v2.2.0 - Barra de abas usa ModalTabsOverflowBar (botão "...", sem rolagem); aba O.S renomeada
 * RELEASE: 2026-10-17 v2.1.0 - Nova aba "Ordens de serviço" (módulo O.S. + permissão os_list)
 * RELEASE: 2026-09-03 v1.10.0 - Contador de fluxos na aba "Fluxos de trabalho" (prop workflowCount)
 * RELEASE: 2026-09-03 v1.9.1 - Rótulo da aba encurtado para "Fluxos" e sem quebra de linha
 * RELEASE: 2026-10-16 v2.0.0 - Nova aba "Remessas" (Logística, permissão log_shipments)
 * RELEASE: 2026-09-03 v1.9.0 - Nova aba "Fluxos de trabalho" (módulo Workflow)
 * RELEASE: 2026-02-16 - Adicionada aba Contratos
 */
import { ModalTabsOverflowBar } from '@/components/common/ModalTabsOverflowBar';
import React from 'react';
import { 
  LayoutDashboard, 
  MessageSquare, 
  Package, 
  Clock,
  FileSignature,
  Wallet,
  ListChecks,
  Zap,
  Paperclip,
  Car,
  FileBadge,
  Workflow,
  Truck,
  Wrench,
} from 'lucide-react';

// Tipo das abas (v1.6.0: 'attachments'; v1.7.0: 'vehicles'; v1.8.0: 'proposals')
export type DealModalTab = 'summary' | 'chat' | 'products' | 'activities' | 'attachments' | 'proposals' | 'contracts' | 'charges' | 'tasks' | 'cadence' | 'vehicles' | 'workflow' | 'shipments' | 'service_orders';



// Props do componente
interface DealModalTabsProps {
  activeTab: DealModalTab;
  onTabChange: (tab: DealModalTab) => void;
  chatCount?: number;
  productsCount?: number;
  contractsCount?: number;
  chargesCount?: number;
  /** v1.4.0: contador de tarefas vinculadas */
  tasksCount?: number;
  /** v1.6.0: contador de anexos agregados da timeline */
  attachmentsCount?: number;
  /** Indica se o módulo financeiro está habilitado para o tenant */
  financialEnabled?: boolean;
  /** Indica se o módulo de tarefas está habilitado para o tenant */
  tasksEnabled?: boolean;
  /** v1.3.0: Permissão de visualização da aba financeira (financial_charges.can_view) */
  canViewCharges?: boolean;
  /** v1.4.0: Permissão de visualização da aba contratos (crm_contracts.can_view) */
  canViewContracts?: boolean;
  /** v1.5.0: indica se o negócio possui cadência ativa (mostra a aba) */
  hasCadence?: boolean;
  /** v1.5.0: contagem de passos atrasados para badge */
  cadenceOverdueCount?: number;
  /** v1.7.0: indica se o módulo de veículos está habilitado no tenant */
  vehiclesEnabled?: boolean;
  /** v1.7.0: contador de veículos vinculados ao negócio */
  vehiclesCount?: number;
  /** v1.8.0: contador de propostas comerciais do negócio */
  proposalsCount?: number;
  /** v1.8.0: permissão de visualização da aba Propostas (crm_proposals.can_view) */
  canViewProposals?: boolean;
  /** v1.9.0: módulo Workflow ligado no tenant (habilita a aba Fluxos de trabalho) */
  workflowEnabled?: boolean;
  /** v1.10.0: total de fluxos do negócio (ativos + concluídos + cancelados) */
  workflowCount?: number;
  /** Aba Remessas habilitada (permissão log_shipments). */
  shipmentsEnabled?: boolean;
  /** Total de remessas vinculadas ao negócio. */
  shipmentsCount?: number;
  /** v2.1.0: aba Ordens de serviço habilitada (módulo O.S. + permissão os_list) */
  serviceOrdersEnabled?: boolean;
  /** v2.1.0: total de O.S. vinculadas ao negócio */
  serviceOrdersCount?: number;
}

// Configuração das abas (v1.6.0: 'attachments'; v1.7.0: 'vehicles')
const tabsConfig: Array<{
  id: DealModalTab;
  label: string;
  icon: typeof LayoutDashboard;
  countKey?: 'chatCount' | 'productsCount' | 'contractsCount' | 'chargesCount' | 'tasksCount' | 'cadenceOverdueCount' | 'attachmentsCount' | 'vehiclesCount' | 'proposalsCount' | 'workflowCount' | 'shipmentsCount' | 'serviceOrdersCount';
}> = [
  { id: 'summary', label: 'Resumo', icon: LayoutDashboard },
  // v1.9.0: aba dedicada aos fluxos de trabalho (só com o módulo Workflow ligado)
  { id: 'workflow', label: 'Fluxos de trabalho', icon: Workflow, countKey: 'workflowCount' },
  { id: 'cadence', label: 'Cadência', icon: Zap, countKey: 'cadenceOverdueCount' },
  { id: 'chat', label: 'Chat', icon: MessageSquare, countKey: 'chatCount' },
  { id: 'products', label: 'Produtos', icon: Package, countKey: 'productsCount' },
  { id: 'vehicles', label: 'Veículos', icon: Car, countKey: 'vehiclesCount' },
  { id: 'proposals', label: 'Propostas', icon: FileBadge, countKey: 'proposalsCount' },
  { id: 'contracts', label: 'Contratos', icon: FileSignature, countKey: 'contractsCount' },
  { id: 'charges', label: 'Financeiro', icon: Wallet, countKey: 'chargesCount' },
  { id: 'tasks', label: 'Tarefas', icon: ListChecks, countKey: 'tasksCount' },
  // v2.1.0: O.S. vinculadas ao negócio (mesma tabela da listagem de O.S.)
  { id: 'service_orders', label: 'O.S', icon: Wrench, countKey: 'serviceOrdersCount' },
  // Logística: remessas vinculadas ao negócio (mesmo componente da aba da O.S.)
  { id: 'shipments', label: 'Remessas', icon: Truck, countKey: 'shipmentsCount' },
  { id: 'activities', label: 'Atividades', icon: Clock },
  { id: 'attachments', label: 'Anexos', icon: Paperclip, countKey: 'attachmentsCount' },
];


/**
 * Sistema de abas do modal (36px)
 */
export function DealModalTabs({
  activeTab,
  onTabChange,
  chatCount,
  productsCount,
  contractsCount,
  chargesCount,
  tasksCount,
  attachmentsCount,
  financialEnabled = false,
  tasksEnabled = false,
  canViewCharges = true,
  canViewContracts = true,
  hasCadence = false,
  cadenceOverdueCount,
  vehiclesEnabled = false,
  vehiclesCount,
  proposalsCount,
  canViewProposals = true,
  workflowEnabled = false,
  workflowCount,
  shipmentsEnabled = false,
  shipmentsCount,
  serviceOrdersEnabled = false,
  serviceOrdersCount,
}: DealModalTabsProps) {
  const counts = { chatCount, productsCount, contractsCount, chargesCount, tasksCount, cadenceOverdueCount, attachmentsCount, vehiclesCount, proposalsCount, workflowCount, shipmentsCount, serviceOrdersCount };


  // Filtra abas conforme módulos habilitados no tenant + permissões granulares
  const visibleTabs = tabsConfig.filter(tab => {
    if (tab.id === 'charges' && (!financialEnabled || !canViewCharges)) return false;
    if (tab.id === 'contracts' && !canViewContracts) return false;
    if (tab.id === 'tasks' && !tasksEnabled) return false;
    if (tab.id === 'cadence' && !hasCadence) return false;
    if (tab.id === 'vehicles' && !vehiclesEnabled) return false;
    // v1.8.0: aba Propostas depende da permissão granular crm_proposals
    if (tab.id === 'proposals' && !canViewProposals) return false;
    // v1.9.0: aba Fluxos de trabalho depende do módulo Workflow no tenant
    if (tab.id === 'workflow' && !workflowEnabled) return false;
    // Logística: aba Remessas só com permissão log_shipments
    if (tab.id === 'shipments' && !shipmentsEnabled) return false;
    // v2.1.0: aba Ordens de serviço só com módulo O.S. + permissão os_list
    if (tab.id === 'service_orders' && !serviceOrdersEnabled) return false;
    return true;
  });

  // v2.2.0: abas excedentes vão para o botão "..." (sem barra de rolagem), igual ao modal de Veículos
  return (
    <ModalTabsOverflowBar
      items={visibleTabs.map((tab) => ({
        id: tab.id,
        label: tab.label,
        icon: tab.icon,
        count: tab.countKey ? counts[tab.countKey] : undefined,
      }))}
      activeId={activeTab}
      onChange={(id) => onTabChange(id as DealModalTab)}
    />
  );
}
